import { searchAllGames } from '@intelligence/services/filter';
import { generateGenreSelectValue } from '@intelligence/v3/utils/genre';
import { handleMetricRangeFilter } from '@intelligence/utils/filterUtils';
import { REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';
import { formatGenerePageFunc, formatRevenueRangePageFunc } from '@intelligence/components/Filter/util';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { fetchFilterTagNameDetails } from '@intelligence/v3/services/topCharts';

import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

// Sensor Tower Tag 筛选器映射：URL 参数名 -> 筛选器 key
const sensorTowerTagMap: IRecord<string> = {
  art_style: 'sensor_tower_tag_art_style',
  camera_pov: 'sensor_tower_tag_camera_pov',
  product_model: 'sensor_tower_tag_product_model',
  setting: 'sensor_tower_tag_setting',
  tags: 'sensor_tower_tag_tags',
  theme: 'sensor_tower_tag_theme',
  monetization: 'sensor_tower_tag_monetization',
};

/**
 * 根据游戏 ID 数组获取游戏详情
 * @param gameIds - 游戏 ID 数组
 * @returns 游戏详情对象数组
 */
export const fetchGameDetails = async (gameIds: string[]) => {
  if (!gameIds || gameIds.length === 0) {
    return [];
  }

  try {
    const response = await searchAllGames({
      entity_type: 'mobile',
      game_type: 'current_game',
      keyword: '',
      es_ids: gameIds,
      page_num: 1,
      page_size: gameIds.length,
    });
    return response?.data?.game_list || [];
  } catch (error) {
    console.error('[URL Filter Debug] 获取游戏详情失败:', error);
    return [];
  }
};

/**
 * Range filter configuration
 */
interface RangeFilterConfig {
  metric: string;
  filterKey: string;
  legacyMinParams?: string[];
  legacyMaxParams?: string[];
}

const normalizeStringArrayValue = (raw: any): string[] => {
  if (Array.isArray(raw)) {
    return raw.map((item) => `${item}`.trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    return raw.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const rangeFilterConfigs: RangeFilterConfig[] = [
  {
    metric: 'arpu',
    filterKey: filterKeyMap.ARPU_RANGE,
    legacyMinParams: ['arpu_min_dataai', 'arpu_min'],
    legacyMaxParams: ['arpu_max_dataai', 'arpu_max'],
  },
  {
    metric: 'au',
    filterKey: filterKeyMap.ACTIVE_USERS_RANGE,
    legacyMinParams: ['au_min_dataai', 'au_min'],
    legacyMaxParams: ['au_max_dataai', 'au_max'],
  },
  {
    metric: 'download',
    filterKey: filterKeyMap.DOWNLOADS_RANGE,
    legacyMinParams: ['download_min_dataai', 'downloads_min'],
    legacyMaxParams: ['download_max_dataai', 'downloads_max'],
  },
  {
    metric: 'revenue',
    filterKey: filterKeyMap.REVENUE_RANGE,
    legacyMinParams: ['revenue_min_dataai', 'revenue_min'],
    legacyMaxParams: ['revenue_max_dataai', 'revenue_max'],
  },
];

/**
 * Parse a single range filter from params
 * @param params - Parameter object from cond API
 * @param config - Range filter configuration
 * @returns Range filter value or null if no data
 */
const parseRangeFilter = (params: IRecord, config: RangeFilterConfig): { min?: number; max?: number } | null => {
  const { metric, legacyMinParams = [], legacyMaxParams = [] } = config;

  // Extract SensorTower range
  const stRangeObj = params?.[`sensortower__${metric}`];
  const stMin = stRangeObj?.min ?? params?.[`${metric}_min_st`];
  const stMax = stRangeObj?.max ?? params?.[`${metric}_max_st`];

  // Extract data.ai/App Annie range with legacy param fallbacks
  const aaRangeObj = params?.[`appannie__${metric}`];
  let aaMin = aaRangeObj?.min;
  let aaMax = aaRangeObj?.max;

  // Check legacy params for min
  for (const legacyParam of legacyMinParams) {
    if (aaMin === undefined && params?.[legacyParam] !== undefined) {
      aaMin = params[legacyParam];
      break;
    }
  }

  // Check legacy params for max
  for (const legacyParam of legacyMaxParams) {
    if (aaMax === undefined && params?.[legacyParam] !== undefined) {
      aaMax = params[legacyParam];
      break;
    }
  }

  // Check if any values exist
  if (stMin === undefined && stMax === undefined && aaMin === undefined && aaMax === undefined) {
    return null;
  }

  // Prefer SensorTower, fallback to data.ai
  const minVal = stMin !== undefined ? stMin : aaMin;
  const maxVal = stMax !== undefined ? stMax : aaMax;

  return {
    min: minVal !== undefined ? Number(minVal) : undefined,
    max: maxVal !== undefined ? Number(maxVal) : undefined,
  };
};

/**
 * 从 URL query 参数中解析筛选器参数（同步部分）
 * @param params - 参数对象
 * @param props - 其他参数
 * @param props.genreOptionsMap - 品类options映射
 * @returns 筛选器参数对象和游戏 ID 数组
 */
export const parseUrlFilterParams = async (
  params: IRecord,
  props?: { genreOptionsMap?: IRecord<IOptionWithChildren[]> },
): Promise<any> => {
  const filterParams: any = {};
  const gameIds = normalizeStringArrayValue(params?.game_ids);

  const { genreOptionsMap } = props || {};

  // 如果有游戏 ID，异步获取游戏详情
  if (gameIds.length > 0) {
    const gameDetails = await fetchGameDetails(gameIds);
    filterParams.allGames = {
      value: gameIds,
      keywords: '',
      isSearchAll: true,
      checkedList: gameDetails || [],
    };
  }

  // 解析公司筛选器参数（company 字段，多个公司 id 用英文逗号分隔）
  const companyIds = normalizeStringArrayValue(params?.company);
  if (companyIds.length > 0) {
    filterParams[filterKeyMap.ALL_COMPANYS] = {
      value: companyIds,
    };
  }

  const publisherClasses = normalizeStringArrayValue(params?.publisher_class || params?.publisher_classes);
  if (publisherClasses.length > 0) {
    filterParams[filterKeyMap.PUBLISHER_CLASS] = {
      value: publisherClasses,
    };
  }

  const developers = normalizeStringArrayValue(params?.developer || params?.developers);
  if (developers.length > 0) {
    filterParams[filterKeyMap.DEVELOPER] = {
      value: developers,
    };
  }

  const publisherRegions = normalizeStringArrayValue(params?.publisher_region || params?.publisher_regions);
  if (publisherRegions.length > 0) {
    filterParams[filterKeyMap.PUBLISHER_REGION] = {
      type: 'market',
      regions: [],
      markets: publisherRegions,
    };
  }

  const revenueScopeRaw = Array.isArray(params?.[filterKeyMap.REVENUE_SCOPE])
    ? params?.[filterKeyMap.REVENUE_SCOPE]?.[0]
    : params?.[filterKeyMap.REVENUE_SCOPE];
  const revenueScopeValue = typeof revenueScopeRaw === 'string'
    ? revenueScopeRaw
    : '';
  const validValues = Object.values(REVENUE_SCOPE_VALUES);

  if (validValues.includes(revenueScopeValue as any)) {
    filterParams[filterKeyMap.REVENUE_SCOPE] = {
      value: revenueScopeValue,
    };
  }

  // 解析所有 Sensor Tower Tag 参数
  Object.entries(sensorTowerTagMap).forEach(([urlParam, filterKey]) => {
    const values = params?.[urlParam] || [];
    if (values.length > 0) {
      filterParams[filterKey] = {
        value: values,
      };
    }
  });

  // Parse all range filters using configuration
  rangeFilterConfigs.forEach((config) => {
    const rangeValue = parseRangeFilter(params, config);
    if (rangeValue) {
      const configFilterKey = config?.filterKey || '';
      const configFilterValue = { value: rangeValue };
      // @ts-ignore
      filterParams[configFilterKey] = formatRevenueRangePageFunc(configFilterValue, configFilterKey);
    }
  });
  Object.assign(filterParams, handleMetricRangeFilter(filterParams));

  // 解析品类筛选器参数（返回 UI 格式，供 setValueAndConfirm 使用）
  const genreParams = {
    ...params?.[filterKeyMap.GENRETOR],
    optionsMap: genreOptionsMap,
  };
  const genreSelectValue = generateGenreSelectValue(genreParams);
  if (genreSelectValue) {
    filterParams[filterKeyMap.GENRETOR] = formatGenerePageFunc(
      {
        ...genreSelectValue,
        options: genreOptionsMap?.[genreSelectValue?.source] || [],
      },
      '',
      { isStdGenreValuesHandler: true },
    );
  }

  // 解析所有app magic tag参数
  filterKeyMap.APPMAGIC_TAG_KEYS.forEach((appMagicTagKey) => {
    const values = params?.[appMagicTagKey] || [];
    if (values.length > 0) {
      filterParams[appMagicTagKey] = {
        value: values,
      };
    }
  });

  // 解析 Steam Tags / Game Mode / Game Engine 参数
  // 兼容数组格式 ["Anime"] 和逗号分隔字符串格式 "indie,action,adventure"
  // 同时解析 steam_tag_relation / game_engine_relation 为筛选器的 logic 字段
  const extraTagRelationMap: IRecord<string> = {
    [filterKeyMap.STEAM_TAGS]: 'steam_tag_relation',
    [filterKeyMap.GAME_ENGINE]: 'game_engine_relation',
  };
  const extraTagKeys = [filterKeyMap.STEAM_TAGS, filterKeyMap.GAME_MODE, filterKeyMap.GAME_ENGINE];
  extraTagKeys.forEach((tagKey) => {
    const raw = params?.[tagKey];
    if (!raw) return;
    const values = typeof raw === 'string' ? raw.split(',').map((s: string) => s.trim()).filter(Boolean) : raw;
    if (values.length > 0) {
      const relationKey = extraTagRelationMap[tagKey];
      const logic = relationKey ? (params?.[relationKey] || '').toUpperCase() : '';
      filterParams[tagKey] = {
        value: values,
        ...(logic ? { logic } : {}),
      };
    }
  });

  return filterParams;
};

/**
 * 解析filter_tag_name配置的筛选条件信息
 * @param filterTagName - filter_tag_name的值
 * @param props - 其他参数
 * @param props.genreOptionsMap - 品类options映射
 * @return 筛选器参数对象
 */
export const analysisFilterParams = async (
  filterTagName?: string,
  props?: { genreOptionsMap?: IRecord<IOptionWithChildren[]> },
): Promise<IRecord> => {
  const filterTagNameTrim = filterTagName?.trim() || '';

  if (!filterTagNameTrim) {
    return {};
  }

  // Decode the key for API call and lookup (the response key will be decoded)
  const decodedKey = decodeURIComponent(filterTagNameTrim);

  const filterTagNameDetailsRes = await fetchFilterTagNameDetails({
    filter_tag_name_details: {
      target: 'intelligence_filter_tags',
      params: {
        entity_type: 'mobile',
        keys: [decodedKey],
      },
    },
  });

  // Use decoded key for lookup since API response uses decoded key
  const filterTagNameDetails = filterTagNameDetailsRes?.data?.filter_tag_name_details?.[decodedKey] || {};

  const parsedParams = await parseUrlFilterParams(filterTagNameDetails, props);

  return parsedParams;
};
